import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Menu } from '@base-ui/react/menu'
import { Building2, Check, ChevronsUpDown } from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch } from '@/api/client'
import { useAuth } from '@/app/auth-context'
import type { Organization } from '@/domain'
import { orgRoleLabel } from './org-labels'
import { GROUP_LABEL_STYLE, MENU_ITEM_STYLE, POPUP_STYLE } from './popup-style'

/**
 * Working org switcher: lists orgs from /api/v1/orgs, persists the active
 * org to sessionStorage `it.orgId`, and invalidates every query on switch
 * so all org-scoped data refetches.
 */

const ORG_KEY = 'it.orgId'

function readStoredOrgId(): string | null {
  try {
    return sessionStorage.getItem(ORG_KEY)
  } catch {
    return null
  }
}

function OrgGlyph({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: 'var(--r-xs)',
        background: 'color-mix(in srgb, var(--accent-500) 16%, transparent)',
        color: 'var(--accent-700)',
        display: 'grid',
        placeItems: 'center',
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {name.slice(0, 2).toUpperCase()}
    </span>
  )
}

export function OrgSwitcher() {
  const { session } = useAuth()
  const queryClient = useQueryClient()
  const [orgId, setOrgId] = useState<string | null>(
    () => readStoredOrgId() ?? session?.orgId ?? null,
  )

  const { data } = useQuery({
    queryKey: ['orgs'],
    queryFn: () => apiFetch<Organization[]>({ url: '/api/v1/orgs' }),
    staleTime: 5 * 60_000,
    retry: false,
  })
  const orgs = Array.isArray(data) ? data : []
  const current = orgs.find((o) => o.id === orgId) ?? orgs[0]

  const switchOrg = (org: Organization) => {
    if (org.id === current?.id) return
    try {
      sessionStorage.setItem(ORG_KEY, org.id)
    } catch {
      // Session-only persistence is best-effort.
    }
    setOrgId(org.id)
    void queryClient.invalidateQueries()
    toast.success(`Switched to ${org.name}`)
  }

  const displayName = current?.name ?? session?.orgId ?? 'Organization'

  return (
    <Menu.Root>
      <Menu.Trigger
        className="it-icon-btn it-shell-btn"
        aria-label={`Switch organization — current: ${displayName}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 32,
          width: 'auto',
          padding: '0 12px',
          borderRadius: 'var(--r-sm)',
          border: '1px solid var(--border)',
          background: 'transparent',
          color: 'var(--text)',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: 13,
          fontWeight: 500,
          minWidth: 150,
          maxWidth: 210,
        }}
      >
        <OrgGlyph name={displayName} />
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {displayName}
        </span>
        <ChevronsUpDown size={13} color="var(--text-faint)" aria-hidden />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="start" sideOffset={6} style={{ zIndex: 60 }}>
          <Menu.Popup style={{ ...POPUP_STYLE, minWidth: 260 }}>
            {/* Menu.GroupLabel throws outside a Menu.Group (Base UI 1.5). */}
            <Menu.Group>
              <Menu.GroupLabel style={GROUP_LABEL_STYLE}>Organizations</Menu.GroupLabel>
              {orgs.length === 0 && (
                <div style={{ ...MENU_ITEM_STYLE, color: 'var(--text-mute)', cursor: 'default' }}>
                  <Building2 size={14} aria-hidden />
                  No organizations available
                </div>
              )}
              {orgs.map((org) => {
                const active = org.id === current?.id
                return (
                  <Menu.Item
                    key={org.id}
                    className="it-menu-item"
                    style={MENU_ITEM_STYLE}
                    onClick={() => switchOrg(org)}
                  >
                    <OrgGlyph name={org.name} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          color: 'var(--text)',
                          fontWeight: active ? 600 : 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {org.name}
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--text-mute)' }}>
                        {orgRoleLabel(org.role)}
                      </span>
                    </span>
                    {active && <Check size={14} color="var(--brand-500)" aria-hidden />}
                  </Menu.Item>
                )
              })}
            </Menu.Group>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
